import { useState, useCallback, useMemo } from "react";
import { useUser } from "@clerk/nextjs";
import { UserResource } from "@clerk/types";
import { Role } from "../types/onboarding";
import {
  StudentEmailError,
  StudentEmailErrorType,
} from "../types/studentEmailErrors";
import { getUserStudentEmail, emailHasStudentDomain } from "helpers/email";
import { isStudentRole } from "../utils/roleUtils";

interface UseRoleAndStudentEmailOptions {
  initialRole?: Role | null;
  suggestRoleFromEmail?: boolean;
}

interface UseRoleAndStudentEmailReturn {
  // State
  selectedRole: Role | null;
  studentEmail: string;
  studentEmailError: StudentEmailErrorType | null;
  showVerification: boolean;
  unverifiedEmail: string | null;
  existingStudentEmail: string | null;

  // Derived
  needsStudentEmail: boolean;
  suggestedRole: Role | undefined;

  // Handlers
  handleRoleSelect: (role: Role) => void;
  handleStudentEmailChange: (email: string) => void;
  setShowVerification: (show: boolean) => void;
  setUnverifiedEmail: (email: string | null) => void;
  setStudentEmailError: (error: StudentEmailErrorType | null) => void;
  clearStudentEmail: () => void;

  // Utilities
  prepareForVerification: () => void;
  resetVerification: () => void;
  resetEmailInput: () => void;
}

/**
 * Shared hook for managing role selection and student email verification
 * Used by both onboarding and claim student discount flows
 *
 * @param options Configuration options for the hook
 * @returns Hook state and handlers
 */
export function useRoleAndStudentEmail(
  options: UseRoleAndStudentEmailOptions = {}
): UseRoleAndStudentEmailReturn {
  const { initialRole = null, suggestRoleFromEmail = false } = options;
  const { user } = useUser();

  // Check for existing student email
  const existingStudentEmail = useMemo(() => {
    return user ? getUserStudentEmail(user as unknown as UserResource) : null;
  }, [user]);

  // Suggest role based on existing student email
  const suggestedRole: Role | undefined = useMemo(() => {
    return suggestRoleFromEmail && existingStudentEmail
      ? "undergrad"
      : undefined;
  }, [suggestRoleFromEmail, existingStudentEmail]);

  // Core state
  const [selectedRole, setSelectedRole] = useState<Role | null>(
    initialRole || suggestedRole || null
  );
  const [studentEmail, setStudentEmail] = useState("");
  const [studentEmailError, setStudentEmailError] =
    useState<StudentEmailErrorType | null>(null);

  // Verification state
  const [showVerification, setShowVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);

  // Initialize suggested role if needed
  useState(() => {
    if (suggestedRole && !selectedRole && !initialRole) {
      setSelectedRole(suggestedRole);
    }
  });

  // Determine if student email is needed
  const needsStudentEmail = useMemo(() => {
    return isStudentRole(selectedRole) && !existingStudentEmail;
  }, [selectedRole, existingStudentEmail]);

  // Handle role selection
  const handleRoleSelect = useCallback((role: Role) => {
    setSelectedRole(role);
  }, []);

  // Handle student email change
  const handleStudentEmailChange = useCallback(
    (email: string) => {
      setStudentEmail(email);

      // Clear error when user modifies the email
      if (studentEmailError) {
        setStudentEmailError(null);
      }
    },
    [studentEmailError]
  );

  // Clear student email and error
  const clearStudentEmail = useCallback(() => {
    setStudentEmail("");
    setStudentEmailError(null);
  }, []);

  // Prepare for verification
  const prepareForVerification = useCallback(() => {
    if (studentEmail && emailHasStudentDomain(studentEmail)) {
      setUnverifiedEmail(studentEmail);
      setShowVerification(true);
    }
  }, [studentEmail]);

  // Reset verification state (go back to email input)
  const resetVerification = useCallback(() => {
    setShowVerification(false);
    setUnverifiedEmail(null);
  }, []);

  // Reset email input completely
  const resetEmailInput = useCallback(() => {
    setShowVerification(false);
    setStudentEmail("");
    setStudentEmailError(null);
    setUnverifiedEmail(null);
  }, []);

  return {
    // State
    selectedRole,
    studentEmail,
    studentEmailError,
    showVerification,
    unverifiedEmail,
    existingStudentEmail,

    // Derived
    needsStudentEmail,
    suggestedRole,

    // Handlers
    handleRoleSelect,
    handleStudentEmailChange,
    setShowVerification,
    setUnverifiedEmail,
    setStudentEmailError,
    clearStudentEmail,

    // Utilities
    prepareForVerification,
    resetVerification,
    resetEmailInput,
  };
}
